/**
 * Right-side panel that lists the per-test-case git history for the
 * unified Tests page (#542 follow-up). Mockup: docs/mockups/script-version-history.html.
 *
 * The parent (`TestDetail`) conditionally mounts this component — so
 * closing and re-opening the panel for the same test case will
 * remount and re-fetch. That's fine for v1 (history is small and
 * rarely re-opened in rapid succession); a future caching layer
 * would lift the fetch out of the mount cycle. Filter chips trim
 * the list client-side by `CommitPhase` bucket. "Manual" matches
 * both `manual` (real user) and `manual-anon` (legacy `ui-edit`
 * author) — operators don't care about that distinction for
 * filtering.
 *
 * Restore is intentionally out of scope here — it's a write path
 * landing in a fast-follow PR. The mockup's per-row "Restore"
 * button stays unwired for now.
 */
import { useEffect, useMemo, useState } from 'react'
import { testCasesAPI, type CommitPhase, type TestCaseHistoryEntry } from '@/lib/api'

const PHASE_LABEL: Record<CommitPhase, string> = {
  planner: 'Planner',
  generator: 'Generator',
  executor: 'Executor',
  healer: 'Healer',
  system: 'System',
  'manual-anon': 'Manual',
  manual: 'Manual',
}

/** Tailwind classes per phase. Dark + light variants — matches the
 * existing color language in the Tests page (priority chips,
 * health dots). */
const PHASE_CLASSES: Record<CommitPhase, string> = {
  generator:     'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  healer:        'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  planner:       'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  executor:      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  system:        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  manual:        'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  'manual-anon': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
}

/** Filter buckets shown as chips. "Manual" collapses `manual` +
 * `manual-anon` so the chip count matches operator mental model.
 * `planner` is intentionally absent — the server filters planner
 * commits out of the history result (planner-only edits touch yaml,
 * not the spec, so the spec-pane diff has nothing meaningful to show). */
type FilterChip = 'all' | 'manual' | 'generator' | 'healer'
const CHIPS: FilterChip[] = ['all', 'manual', 'generator', 'healer']

function matchesChip(entry: TestCaseHistoryEntry, chip: FilterChip): boolean {
  if (chip === 'all') return true
  if (chip === 'manual') return entry.phase === 'manual' || entry.phase === 'manual-anon'
  return entry.phase === chip
}

export interface HistoryPanelProps {
  /** TestCase _id — the route uses this directly. */
  testCaseId: string
  /** Currently-selected commit SHA (full or short). null means no
   * selection yet; the right pane shows the current Monaco. */
  selectedSha: string | null
  onSelect: (sha: string | null) => void
  /** Triggered by the toolbar's close affordance; the parent owns
   * the open/closed state. */
  onClose: () => void
  /** Opens the restore-confirm flow with the chosen non-current
   * revision. HistoryPanel passes both the row metadata and the
   * current commit's sha so the dialog can detect plan divergence
   * via two parallel revision fetches without needing a `HEAD`
   * ref-lookup path on the server. */
  onRestore?: (revision: TestCaseHistoryEntry, currentSha: string) => void
}

export function HistoryPanel({ testCaseId, selectedSha, onSelect, onClose, onRestore }: HistoryPanelProps) {
  const [entries, setEntries] = useState<TestCaseHistoryEntry[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeChip, setActiveChip] = useState<FilterChip>('all')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    testCasesAPI
      .getHistory(testCaseId)
      .then((data) => {
        if (cancelled) return
        setEntries(data)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load history')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [testCaseId])

  const filtered = useMemo(() => {
    if (!entries) return []
    return entries.filter((e) => matchesChip(e, activeChip))
  }, [entries, activeChip])

  const currentSha = entries?.[0]?.fullSha

  return (
    <aside className="w-80 flex-none flex flex-col bg-muted/20 border-l">
      <div className="flex-none px-3 py-2 border-b flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Version history</div>
          <div className="text-xs text-muted-foreground">
            {entries ? `${entries.length} commit${entries.length === 1 ? '' : 's'}` : ' '}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
          aria-label="Close history"
        >
          ✕
        </button>
      </div>

      <div className="flex-none flex flex-wrap gap-1 px-3 py-2 border-b" role="group" aria-label="Filter history by author">
        {CHIPS.map((chip) => {
          const active = activeChip === chip
          return (
            <button
              key={chip}
              type="button"
              onClick={() => setActiveChip(chip)}
              aria-pressed={active}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {chip === 'all' ? 'All' : PHASE_LABEL[chip as CommitPhase] ?? chip}
            </button>
          )
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="p-3 text-xs text-muted-foreground">Loading history…</div>
        )}
        {error && (
          <div className="p-3 text-xs text-destructive" role="alert">
            {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-3 text-xs text-muted-foreground">
            {entries && entries.length > 0 ? 'No commits match this filter.' : 'No history yet.'}
          </div>
        )}
        <ul className="px-2 py-1 space-y-0.5">
          {filtered.map((entry) => {
            const isCurrent = entry.fullSha === currentSha
            const isSelected = entry.fullSha === selectedSha
            // Restore is offered only on non-current rows; restoring
            // the row that already IS current is a no-op (and would
            // surface as a confusing empty commit). currentSha is
            // always populated here — the empty `filtered` case
            // returned above.
            const canRestore = !isCurrent && onRestore != null && currentSha != null
            return (
              <li key={entry.fullSha} className="relative group">
                <button
                  type="button"
                  onClick={() => onSelect(isSelected ? null : entry.fullSha)}
                  aria-pressed={isSelected}
                  aria-current={isCurrent ? 'true' : undefined}
                  className={`w-full text-left px-2 py-2 rounded-md border-2 ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : isCurrent
                        ? 'border-emerald-500/40 bg-emerald-500/5'
                        : 'border-transparent hover:bg-muted'
                  }`}
                >
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs font-medium flex-1 truncate">{entry.message}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{entry.sha}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${PHASE_CLASSES[entry.phase]}`}>
                      {PHASE_LABEL[entry.phase]}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatDate(entry.date)}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400">· current</span>
                    )}
                    {entry.phase === 'manual' && (
                      <span className="text-[10px] text-muted-foreground truncate">
                        · {entry.authorName}
                      </span>
                    )}
                  </div>
                </button>
                {canRestore && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRestore!(entry, currentSha!)
                    }}
                    className={`absolute right-2 bottom-2 text-[10px] px-2 py-0.5 rounded border border-border bg-background hover:bg-muted focus:opacity-100 ${
                      isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                    aria-label={`Restore ${entry.sha}`}
                  >
                    Restore
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </aside>
  )
}

/** Short relative-ish formatter — "5m ago" / "2d ago" / "May 2".
 * Keep it dependency-free; the panel only needs human-readable, not
 * locale-aware. */
function formatDate(iso: string): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return iso
  const now = Date.now()
  const diffSec = Math.max(0, Math.round((now - t) / 1000))
  if (diffSec < 60) return `${diffSec}s ago`
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  // Past a week, switch to a calendar form. Same year → "May 2";
  // older → "May 2, 2025".
  const d = new Date(iso)
  const now2 = new Date()
  const sameYear = d.getFullYear() === now2.getFullYear()
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}
